import type { BarSeries } from "../components/charts";
import type { FeeBucket, StatsBreakdownRow, StatsSummary } from "./api";
import { num, periodLabel } from "./format";

/** ตัวช่วยของ Dashboard v3 (`components/dx.tsx`) ที่ไม่ใช่ component */

/** สีของชุดข้อมูลแบบแยกกลุ่ม — token ของ design system เท่านั้น; อันดับ 6 ขึ้นไปรวมเป็น ອື່ນໆ */
export const DX_PALETTE = ["var(--navy-700)", "var(--success)", "var(--gold-500)", "var(--red-600)", "var(--warning)"];
export const OTHER_COLOR = "var(--ink-400)";

/** เงินแบบย่อ แยกตัวเลขกับหน่วย — กลางโดนัท/KPI */
export function moneyParts(v: number): { v: string; u: string } {
  if (Math.abs(v) >= 1_000_000) {
    return { v: (v / 1_000_000).toLocaleString("lo-LA", { maximumFractionDigits: 2 }), u: "ລ້ານ ກີບ" };
  }
  return { v: num(v), u: "ກີບ" };
}

/** ส่งออก CSV (UTF-8 + BOM ให้ Excel อ่านภาษาลาวได้) */
export function downloadCsv(filename: string, head: string[], rows: (string | number)[][]) {
  const cell = (v: string | number) => {
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const text = "\uFEFF" + [head, ...rows].map((r) => r.map(cell).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** เงินแบบย่อเป็นข้อความเดียว เช่น `94.64 ລ້ານ ກີບ` */
export const moneyText = (v: number) => {
  const m = moneyParts(v);
  return `${m.v} ${m.u}`;
};

/**
 * พื้นที่ "ຍັງບໍ່ເປີດໃຊ້" = ไม่มีเจ้าหน้าที่ประจำตาแสงเลย และไม่มีเรื่องเข้า/เงินเข้า
 * จอต้องแสดงคำนี้ ไม่ใช่ 0 (ยังไม่มีข้อมูล ≠ ศูนย์) · บ้านไม่มีเจ้าหน้าที่ของตัวเอง จึงดูแค่ยอด
 */
export const isIdle = (r: StatsBreakdownRow) =>
  r.tasaengs > 0 && r.tasaengs_staffed === 0 && r.requests === 0 && r.in_flight === 0 && r.declared_fee_lak === 0;

/** แถว CSV ของตารางสรุปตามพื้นที่ + แถวรวม (ตัวเลขดิบ ไม่ย่อ) */
export function breakdownCsv(
  rows: StatsBreakdownRow[],
  nameOf: (r: StatsBreakdownRow) => string,
  totalLabel: string,
  summary: StatsSummary | undefined,
  finance = true,
): { head: string[]; rows: (string | number)[][] } {
  const days = (s: number) => (s > 0 ? (s / 86400).toFixed(1) : "");
  const line = (name: string, r: { requests: number; issued: number; fee: number; avg: number; inFlight: number; overdue: number }) => [
    name, r.requests, r.issued, r.fee, days(r.avg), r.inFlight, r.overdue,
  ];
  const body = [...rows]
    .sort((a, b) => b.requests - a.requests)
    .map((r) =>
      line(nameOf(r), {
        requests: r.requests, issued: r.issued, fee: r.declared_fee_lak, avg: r.avg_processing_seconds,
        inFlight: r.in_flight, overdue: r.overdue_over_7d,
      }),
    );
  const total = summary
    ? [line(totalLabel, {
        requests: summary.submitted, issued: summary.issued, fee: summary.declared_fee_lak,
        avg: summary.avg_processing_seconds, inFlight: summary.pending, overdue: summary.overdue_over_7d,
      })]
    : [];
  const head = ["ພື້ນທີ່", "ຈຳນວນຄຳຮ້ອງ", "ເອກະສານອອກ", "ຄ່າທຳນຽມ (ກີບ)", "ເວລາສະເລ່ຍ (ມື້)", "ກຳລັງດຳເນີນການ", "ຄ້າງເກີນ 7 ມື້"];
  const rowsOut = [...body, ...total];
  // ไม่มีสิทธิ์ดูการเงินรวม (DSH-04) — ตัดคอลัมน์ค่าธรรมเนียม (index 3) ออกจากไฟล์ด้วย
  return finance
    ? { head, rows: rowsOut }
    : { head: head.filter((_, i) => i !== 3), rows: rowsOut.map((r) => r.filter((_, i) => i !== 3)) };
}

/** หมวดค่าธรรมเนียมตามระเบียบ (`category_code` ใน document_types) */
const CATEGORY_LABEL_LO: Record<string, string> = {
  A: "ໝວດ ກ.",
  B: "ໝວດ ຂ.",
  C: "ໝວດ ຄ.",
  D: "ໝວດ ງ.",
};
const CATEGORY_COLOR: Record<string, string> = {
  A: "var(--navy-700)",
  B: "var(--red-600)",
  C: "var(--gold-500)",
  D: "var(--navy-500)",
};
/** เอกสารที่ไม่ได้ระบุหมวด ต้องมีที่ยืนของตัวเอง ห้ามหายจากยอดรวม */
const UNCATEGORISED = { label: "ບໍ່ລະບຸໝວດ", color: "var(--ink-400)" };

/** แปลงแถวจาก API เป็นแกน + ซีรีส์ของกราฟแท่ง */
export function feeBarChart(buckets: FeeBucket[], unit: "week" | "month"): { labels: string[]; series: BarSeries[] } {
  const periods: string[] = [];
  for (const b of buckets) {
    if (!periods.includes(b.period_start)) periods.push(b.period_start);
  }
  // หมวดที่ "มีเงินจริง" เท่านั้นถึงจะได้แท่ง — แถวศูนย์ที่ API เติมมาเพื่อกันแกนโหว่
  // มี category_code ว่างกับ payments 0 ซึ่งไม่ใช่หมวดจริง
  const categories: string[] = [];
  for (const b of buckets) {
    if (b.payments > 0 && !categories.includes(b.category_code)) categories.push(b.category_code);
  }
  categories.sort();

  const series: BarSeries[] = categories.map((code) => ({
    label: CATEGORY_LABEL_LO[code] ?? UNCATEGORISED.label,
    color: CATEGORY_COLOR[code] ?? UNCATEGORISED.color,
    values: periods.map((p) => {
      const hit = buckets.find((b) => b.period_start === p && b.category_code === code && b.payments > 0);
      return hit?.amount_lak ?? 0;
    }),
  }));
  return { labels: periods.map((p) => periodLabel(p, unit)), series };
}

/** ข้อความของการ์ด ຍື່ນຜ່ານແອັບ — ระบบยังไม่ได้แยกว่าคำร้องมาจากแอปหรือเคาน์เตอร์ */
export const APP_SHARE_NA = "ລະບົບຍັງບໍ່ໄດ້ບັນທຶກຊ່ອງທາງການຍື່ນ (ແອັບ / ເຄົາເຕີ)";
